import { useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { RiCloseLine, RiSearchLine } from '@remixicon/react';
import classNames from 'classnames';

import {
  ButtonIcon,
  FormInput,
  FormInputIcon,
  type FormInputProps,
  Icon,
} from '@spotify-internal/encore-web';

import { useDebounced } from '../../hooks/useDebounced';
import {
  FILTER_SHORTCUT_HOTKEY,
  useFilterShortcut,
} from '../../hooks/useFilterShortcut';
import { useFilters } from './provider';

import styles from './FilterSearch.module.css';

interface FilterSearchProps {
  searchCollapsed?: boolean;
  debounceTime?: number;
  placeholder?: string;
  size?: FormInputProps['size'];
}

export const FilterSearch = ({
  searchCollapsed = false,
  debounceTime = 300,
  placeholder = 'Filter...',
  size = 'small',
}: FilterSearchProps) => {
  const { searchQuery, setQuery } = useFilters();
  const containerRef = useRef<HTMLDivElement>(null);
  const [filterShortcutEnabled] = useFilterShortcut();
  const [expanded, setExpanded] = useState(false);

  const [displayValue, handleChangeDebounced, handleChangeImmediately] =
    useDebounced<string>(searchQuery, setQuery, debounceTime);

  const isCollapsed = searchCollapsed && !expanded && !displayValue;

  useHotkeys(
    FILTER_SHORTCUT_HOTKEY,
    event => {
      if (searchCollapsed && !expanded) {
        event.preventDefault();
        setExpanded(true);
        return;
      }

      const input = containerRef.current?.querySelector<HTMLElement>('input');
      if (!input) return;

      if (document.activeElement === input) return;

      event.preventDefault();
      input.focus();
    },
    { enabled: filterShortcutEnabled, enableOnFormTags: true },
    [searchCollapsed, expanded],
  );

  if (isCollapsed) {
    return (
      <ButtonIcon
        variant="secondary"
        size="small"
        aria-label="search"
        className={styles.collapsedButton}
        onClick={() => setExpanded(true)}
       data-track-source-anchor="f9c2444268;;low">
        <RiSearchLine size={18} />
      </ButtonIcon>
    );
  }

  return (
    <div
      ref={containerRef}
      className={classNames(styles.root, {
        [styles.rootCollapsed]: searchCollapsed,
      })}
    >
      <FormInputIcon
        className={styles.field}
        iconLeading={
          <Icon as={RiSearchLine} size="small" semanticColor="textSubdued" />
        }
        iconTrailing={
          displayValue ? (
            <ButtonIcon
              className={styles.clearButton}
              variant="tertiary"
              size="small"
              aria-label="Clear search"
              onClick={() => handleChangeImmediately('')}
             data-track-source-anchor="f0553d3f5d;;low">
              <RiCloseLine size={16} />
            </ButtonIcon>
          ) : undefined
        }
      >
        <FormInput
          className={styles.input}
          size={size}
          type="search"
          name="filter-bar"
          placeholder={placeholder}
          value={displayValue}
          autoFocus={expanded}
          onChange={e => handleChangeDebounced(e.currentTarget.value)}
          onBlur={() => {
            if (searchCollapsed && !displayValue) setExpanded(false);
          }}
          aria-label="search"
         data-track-source-anchor="915663b383"/>
      </FormInputIcon>
    </div>
  );
};
